import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { stateStyle } from "./healthFormat";

// Pieces shared by the System Health screens.

export const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]";
export const td = "px-3 py-2.5 align-top text-[13px] text-[#374151]";
export const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2667B5]";
export const PAGE = 50;

// Shown on hover, so a developer can see which API route a screen's numbers come from.
// Overview and Server cover the whole platform, so they live under /superadmin.
const ADMIN_ONLY = new Set(["problems", "machine"]);
export const endpoint = (path) => `GET /api/${ADMIN_ONLY.has(path) ? "superadmin/" : ""}system-health/${path}`;

// The buttons at the top of a screen: a dark one for what needs doing, a grey one for the
// rest. Square-cornered like the filters, so the page has one kind of control.
export const primaryButton = `h-9 cursor-pointer rounded-lg bg-[#F3F4F6] px-3.5 text-[13px] font-semibold text-[#111827] hover:bg-[#E5E7EB] ${focusRing}`;
export const dangerButton = `h-9 cursor-pointer rounded-lg bg-[#111827] px-3.5 text-[13px] font-semibold text-white hover:bg-[#1F2937] ${focusRing}`;

export const Loading = () => (
  <div role="status" className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
    <Loader2 className="animate-spin" size={15} /> Loading
  </div>
);

export function Notice({ children, role }) {
  return (
    <div role={role} className="mt-6 rounded-xl border border-[#F5D9A8] bg-[#FFFBEB] p-4 text-[13px] text-[#92400E]">
      {children}
    </div>
  );
}

// A screen's heading takes focus when the screen opens, so a keyboard or screen
// reader user is told where they landed instead of being left on the old page.
export function Heading({ children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);
  return (
    <h2 ref={ref} tabIndex={-1} className={`outline-none ${className}`}>
      {children}
    </h2>
  );
}

// Plain-words warnings that the numbers on a screen may be incomplete.
export function Notes({ notes }) {
  return (notes || []).map((note) => <Notice key={note}>{note}</Notice>);
}

export function FailedToLoad({ error, retry }) {
  return (
    <Notice role="alert">
      Could not load this view{error?.response?.data?.detail ? `: ${error.response.data.detail}` : "."}
      {retry && (
        <button type="button" onClick={retry} className={`ml-3 cursor-pointer font-semibold underline ${focusRing}`}>
          Try again
        </button>
      )}
    </Notice>
  );
}

export const MigrationPending = ({ file = "20261006100000_add_system_health.sql" }) => (
  <Notice>
    System health is not fully set up on this server yet. Ask the developer to run the latest database update, then reload.
    <div className="mt-1 font-mono text-[11px]">{file}</div>
  </Notice>
);

// Tables are wider than a phone. Say so, since a scrolling table looks cut off.
export const ScrollHint = () => <p className="mt-2 text-[12px] text-[#6B7280] sm:hidden">Swipe sideways to see every column.</p>;

// The name in a row is a real button, so the row can be opened from the keyboard.
// Clicking anywhere else on the row does the same thing, for the mouse.
// A number that opens the items behind it. Zero is plain text: nothing to open.
export function Count({ n, onClick, label }) {
  if (!n) return <span className="font-mono tabular-nums text-[#6B7280]">0</span>;
  return (
    <button
      type="button"
      aria-label={`${n} ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`-my-2 inline-flex min-h-10 min-w-10 cursor-pointer items-center justify-center rounded-md px-2 font-mono font-semibold tabular-nums text-[#111827] hover:bg-[#F3F4F6] ${focusRing}`}
    >
      {n}
    </button>
  );
}

export function Badge({ label, tone }) {
  const style = stateStyle(tone);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium" style={{ color: style.text }}>
      <span className="h-2 w-2 rounded-full" style={{ background: style.dot }} />
      {label}
    </span>
  );
}

export function Crumbs({ trail, period }) {
  return (
    <nav aria-label="Where you are" className="mt-5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px]">
      {trail.map((crumb, i) => (
        <span key={`${i}-${crumb.label}`} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden="true" className="text-[#6B7280]">›</span>}
          {crumb.go ? (
            <button type="button" onClick={crumb.go} className={`cursor-pointer rounded px-1 py-2.5 font-medium text-[#2667B5] hover:underline ${focusRing}`}>
              {crumb.label}
            </button>
          ) : (
            <span className="px-1 font-semibold text-[#111827]">{crumb.label}</span>
          )}
        </span>
      ))}
      <span className="ml-auto text-[12px] text-[#6B7280]">{period}</span>
    </nav>
  );
}

// A filter that is switched on, shown as a small tag with × to remove it.
export const pillStyle = (active) => ({
  background: active ? "#EFF5FC" : "#F3F4F6",
  color: active ? "#1F5596" : "#4B5563",
});

export const pillClass = `h-8 cursor-pointer rounded-lg px-2.5 text-[12px] font-semibold hover:brightness-95 ${focusRing}`;

export function Pager({ page, shown, total, onPage }) {
  const first = page * PAGE + 1;
  const buttonClass = `h-10 cursor-pointer rounded-md border border-[#E5E7EB] px-3 disabled:cursor-default disabled:opacity-40 ${focusRing}`;
  return (
    <div className="mt-3 flex items-center justify-between text-[12px] text-[#4B5563]">
      <span>
        {first}–{Math.min(first + shown - 1, total)} of {total}
      </span>
      <span className="flex gap-2">
        <button type="button" disabled={page === 0} onClick={() => onPage(page - 1)} className={buttonClass}>
          Newer
        </button>
        <button type="button" disabled={first + PAGE > total} onClick={() => onPage(page + 1)} className={buttonClass}>
          Older
        </button>
      </span>
    </div>
  );
}

// A bordered white panel: the surface every System health screen is built from.
export const panel = "rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_4px_12px_rgba(16,24,40,0.08)]";

// One look per tone, for the icon chip at the start of a row and the pill at its end.
export const TONE_LOOKS = {
  good: { soft: "#ECFDF3", solid: "#16A34A", ink: "#166534" },
  bad: { soft: "#FEF2F2", solid: "#DC2626", ink: "#991B1B" },
  warn: { soft: "#FFFBEB", solid: "#D97706", ink: "#92400E" },
  idle: { soft: "#F3F4F6", solid: "#6B7280", ink: "#374151" },
};

export function Chip({ tone, children }) {
  const look = TONE_LOOKS[tone] || TONE_LOOKS.idle;
  return (
    <span className="whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold" style={{ background: look.soft, color: look.ink }}>
      {children}
    </span>
  );
}

export function IconChip({ icon: Icon, tone, size = 10 }) {
  const look = TONE_LOOKS[tone] || TONE_LOOKS.idle;
  return (
    <span
      className={`flex flex-none items-center justify-center rounded-xl ${size === 9 ? "h-9 w-9" : "h-10 w-10"}`}
      style={{ background: look.soft, color: look.solid }}
    >
      <Icon size={size === 9 ? 18 : 19} aria-hidden="true" />
    </span>
  );
}

// A row of choices where one is on, as one grey block.
export function Segmented({ label, options, isOn, pick }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-xl bg-[#F3F4F6] p-1" role="group" aria-label={label}>
      {options.map((o) => {
        const on = isOn(o.key);
        return (
          <button
            key={o.key || "all"}
            type="button"
            aria-pressed={on}
            onClick={() => pick(o.key)}
            className={`h-9 cursor-pointer rounded-lg px-3 text-[13px] font-medium ${on ? "bg-white text-[#111827] shadow-[0_1px_2px_rgba(16,24,40,0.08)]" : "text-[#4B5563] hover:text-[#111827]"} ${focusRing}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
